import React, { useEffect, useMemo } from "react";

import { TFunction, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import { StopOutlined } from "@ant-design/icons";
import { Skeleton, Table, Tooltip } from "antd";
import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { Searcher } from "../../components/searcher";
import { ColumnTitle, TableNotification } from "../../components/table";
import {
  airlinesProductivityFetchRequest,
  dailyAirlinesProductivityFetchRequest,
} from "../../store/airlines_productivity/actions";
import { AirlineProductivityItem, AirlineProductivityStatus } from "../../store/airlines_productivity/types";
import { RootState } from "../../store/root_reducer";
import { formatNumber, getSortOrder } from "../../utils/table";

import styles from "./styles.module.scss";

const columns = (t: TFunction<"airlines_productivity_page">, ordering: string, status: AirlineProductivityStatus[]) => {
  return [
    {
      key: "airline",
      title: <ColumnTitle title={t("table.columns.airline.title")} helpText={t("table.columns.airline.helpText")} />,
      render: (item: AirlineProductivityItem) => {
        if (item.airline) {
          if (item.isFiltered) {
            return (
              <Tooltip title={t("table.columns.airline.filterText", { airline: item.airline })}>
                <StopOutlined style={{ marginRight: "5px", color: "#F54759" }} />
                <b style={{ color: "#F54759" }}>{item.airline}</b>
              </Tooltip>
            );
          }
          return item.airline;
        }
        return <Skeleton title={false} active paragraph={{ rows: 1, width: "100%" }} className={styles.skeleton} />;
      },
      width: "15%",
      sorter: true,
      sortOrder: getSortOrder("airline", ordering),
      showSorterTooltip: false,
    },
    {
      key: "clicks",
      title: <ColumnTitle title={t("table.columns.clicks.title")} helpText={t("table.columns.clicks.helpText")} />,
      dataIndex: "clicks",
      render: (value: number, item: AirlineProductivityItem) =>
        value || item.airline ? (
          formatNumber(value)
        ) : (
          <Skeleton title={false} active paragraph={{ rows: 1, width: "100%" }} className={styles.skeleton} />
        ),
      sorter: true,
      width: "15%",
      sortOrder: getSortOrder("clicks", ordering),
      showSorterTooltip: false,
    },
    {
      key: "bookings",
      title: <ColumnTitle title={t("table.columns.bookings.title")} helpText={t("table.columns.bookings.helpText")} />,
      dataIndex: "bookings",
      render: (value: number, item: AirlineProductivityItem) =>
        value || item.airline ? (
          formatNumber(value)
        ) : (
          <Skeleton title={false} active paragraph={{ rows: 1, width: "100%" }} className={styles.skeleton} />
        ),
      sorter: true,
      width: "15%",
      sortOrder: getSortOrder("bookings", ordering),
      showSorterTooltip: false,
    },
    {
      key: "predictedBookings",
      title: (
        <ColumnTitle
          title={t("table.columns.predictedBookings.title")}
          helpText={t("table.columns.predictedBookings.helpText")}
        />
      ),
      dataIndex: "predictedBookings",
      render: (value: number, item: AirlineProductivityItem) =>
        value || item.airline ? (
          formatNumber(value)
        ) : (
          <Skeleton title={false} active paragraph={{ rows: 1, width: "100%" }} className={styles.skeleton} />
        ),
      sorter: true,
      width: "15%",
      sortOrder: getSortOrder("predictedBookings", ordering),
      showSorterTooltip: false,
    },
    {
      key: "str",
      title: <ColumnTitle title={t("table.columns.str.title")} helpText={t("table.columns.str.helpText")} />,
      dataIndex: "str",
      render: (value: number, item: AirlineProductivityItem) =>
        value || item.airline ? (
          formatNumber(value)
        ) : (
          <Skeleton title={false} active paragraph={{ rows: 1, width: "100%" }} className={styles.skeleton} />
        ),
      width: "10%",
      sorter: true,
      sortOrder: getSortOrder("str", ordering),
      showSorterTooltip: false,
    },
    {
      key: "productivity",
      title: (
        <ColumnTitle
          title={t("table.columns.productivity.title")}
          helpText={t("table.columns.productivity.helpText")}
        />
      ),
      filteredValue: status,
      dropdownPrefixCls: "productivity-status",
      filters: [
        {
          text: (
            <>
              <span className={styles.indicator} {...{ status: "high" }} />
              <span>{t("table.columns.productivity.status.high")}</span>
            </>
          ),
          value: "high",
        },
        {
          text: (
            <>
              <span className={styles.indicator} {...{ status: "medium" }} />
              <span>{t("table.columns.productivity.status.medium")}</span>
            </>
          ),
          value: "medium",
        },
        {
          text: (
            <>
              <span className={styles.indicator} {...{ status: "low" }} />
              <span>{t("table.columns.productivity.status.low")}</span>
            </>
          ),
          value: "low",
        },
      ],
      render: (item: AirlineProductivityItem) =>
        item.productivity || item.airline ? (
          <>
            <span className={styles.indicator} {...{ status: item.status }} />
            <span>{formatNumber(item.productivity, 3)}</span>
          </>
        ) : (
          <Skeleton title={false} active paragraph={{ rows: 1, width: "100%" }} className={styles.skeleton} />
        ),
      width: "15%",
      sorter: true,
      sortOrder: getSortOrder("productivity", ordering),
      showSorterTooltip: false,
    },
    {
      key: "minimumProductivity",
      title: (
        <ColumnTitle
          title={t("table.columns.minimumProductivity.title")}
          helpText={t("table.columns.minimumProductivity.helpText")}
        />
      ),
      dataIndex: "minimumProductivity",
      render: (value: number, item: AirlineProductivityItem) => {
        if (value !== undefined && value !== null) {
          return value ? `${formatNumber(value, 3)}` : t("table.columns.minimumProductivity.notSetup");
        }
        return "";
      },
      width: "15%",
      sorter: true,
      sortOrder: getSortOrder("minimumProductivity", ordering),
      showSorterTooltip: false,
    },
  ];
};

export const AirlinesProductivityPage: React.FC = () => {
  const { t, i18n } = useTranslation("airlines_productivity_page");

  const airlinesProductivity = useSelector((state: RootState) => state.AirlinesProductivity);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);

  const [state, setState] = useQueryParams({
    pageSize: withDefault(NumberParam, 10),
    page: withDefault(NumberParam, 1),
    search: withDefault(StringParam, undefined),
    status: withDefault(ArrayParam, [] as AirlineProductivityStatus[]),
    ordering: withDefault(StringParam, "-clicks"),
  });

  const dispatch = useDispatch();

  useEffect(() => {
    setState((currentState) => currentState, "replaceIn");
  }, [setState]);

  useEffect(() => {
    dispatch(
      airlinesProductivityFetchRequest({
        ...state,
        gateId: selectedGate.id,
        status: state.status as AirlineProductivityStatus[],
      })
    );
  }, [dispatch, state, selectedGate]);

  const handleExpandRow = (expanded: boolean, item: AirlineProductivityItem) => {
    if (expanded && !(item.airline in airlinesProductivity.dailyItems)) {
      dispatch(dailyAirlinesProductivityFetchRequest({ airline: item.airline, gateId: selectedGate.id }));
    }
  };

  const handleSearch = (value: string) => {
    setState((currentState) => ({ ...currentState, search: value || undefined, page: 1 }));
  };

  const handleTableChange = (
    { current, pageSize }: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<AirlineProductivityItem> | SorterResult<AirlineProductivityItem>[],
    { action }: TableCurrentDataSource<AirlineProductivityItem>
  ) => {
    switch (action) {
      case "paginate":
        setState((currentState) => ({
          ...currentState,
          page: currentState.pageSize !== pageSize ? 1 : current,
          pageSize,
        }));
        break;
      case "filter": {
        const filterStatuses = (filters.productivity || []) as AirlineProductivityStatus[];
        if (state.status.toString() !== filterStatuses.toString()) {
          setState((currentState) => ({ ...currentState, status: filterStatuses, page: 1 }));
        }
        break;
      }
      case "sort": {
        if (!Array.isArray(sorter)) {
          const { columnKey, order } = sorter;
          if (order) {
            const ordering = `${order === "descend" ? "-" : ""}${columnKey}`;
            setState((currentState) => ({ ...currentState, ordering }));
          } else {
            setState((currentState) => ({ ...currentState, ordering: undefined }));
          }
        }
        break;
      }
      default:
        break;
    }
  };

  const dataSource = useMemo(() => {
    const defaultItems = [{}, {}, {}, {}, {}, {}, {}, {}];
    return airlinesProductivity.items.map((item) => ({
      ...item,
      children: (airlinesProductivity.dailyItems[item.airline] ?? defaultItems).map((dailyItem) => ({
        ...dailyItem,
        airline: dailyItem.date,
      })),
    }));
  }, [airlinesProductivity.dailyItems, airlinesProductivity.items]);

  return (
    <Page title={t("page.title")} description={t("page.description")}>
      <Searcher
        onSearch={handleSearch}
        className={styles.searcher}
        defaultValue={state.search}
        examples={["S7", "UT", "R3"]}
        placeholder={t("search.placeholder")}
      />
      <TableNotification />
      <Table<AirlineProductivityItem>
        className={`ads`}
        bordered
        loading={airlinesProductivity.isLoading}
        columns={columns(t, state.ordering || "", state.status as AirlineProductivityStatus[])}
        dataSource={dataSource}
        onChange={handleTableChange}
        dropdownPrefixCls="airline-productivity"
        rowKey={(record: AirlineProductivityItem) =>
          `airline-productivity-row-${record.airline ?? Math.random().toString(36).substring(7)}`
        }
        footer={() => `${i18n.t("common:table.total")}: ${airlinesProductivity.count}`}
        pagination={{
          total: airlinesProductivity.count,
          current: state.page,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          pageSize: state.pageSize,
        }}
        size="small"
        onExpand={handleExpandRow}
      />
    </Page>
  );
};
